import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { toast } from "sonner";
import { Sparkles, Loader2, Route, Clock, CalendarDays, Truck, FileDown } from "lucide-react";
import { formatDateTime } from "../lib/format";

const PERIODS = [
  ["settimana", "Ultima settimana"], ["mese", "Ultimo mese"], ["3mesi", "Ultimi 3 mesi"], ["anno", "Anno corrente"],
];

const ChartCard = ({ title, icon: Icon, children, testId }) => (
  <Card data-testid={testId} className="border-slate-200 p-5">
    <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /> {title}</h3>
    {children}
  </Card>
);

const TopList = ({ items, testId }) => (
  <ul data-testid={testId} className="space-y-2">
    {items.length === 0 && <li className="text-sm text-slate-400">Nessun dato</li>}
    {items.map((r, i) => (
      <li key={r.name} className="flex items-center justify-between text-sm gap-3">
        <span className="truncate text-slate-700"><span className="text-slate-400 mr-2">{i + 1}.</span>{r.name}</span>
        <span className="font-semibold text-slate-900 shrink-0">{r.count}</span>
      </li>
    ))}
  </ul>
);

const parseNarrative = (text) =>
  text.split("\n").map((raw) => raw.trim()).filter((l) => l && l !== "---").map((raw, i) => {
    const l = raw.replace(/^>\s*/, "");
    const key = `${i}-${l.slice(0, 24)}`;
    if (l.startsWith("### ") || l.startsWith("## ")) return { key, kind: "h", text: l.replace(/^#+\s*/, "") };
    if (/^[-*] /.test(l)) return { key, kind: "li", text: l.slice(2).replace(/\*\*/g, "") };
    return { key, kind: "p", text: l.replace(/\*\*/g, "") };
  });

const Narrative = ({ text }) => {
  const blocks = useMemo(() => parseNarrative(text), [text]);
  return (
    <div data-testid="insights-narrative" className="prose prose-sm max-w-none text-slate-700 space-y-2">
      {blocks.map((b) => {
        if (b.kind === "h") return <h4 key={b.key} className="text-base font-bold text-slate-900 mt-4">{b.text}</h4>;
        if (b.kind === "li") return <p key={b.key} className="pl-4 text-sm">• {b.text}</p>;
        return <p key={b.key} className="text-sm">{b.text}</p>;
      })}
    </div>
  );
};

export default function InsightsPage() {
  const [period, setPeriod] = useState("mese");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/insights/export", { params: { period }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Insights_Nexus_${period}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nell'esportazione PDF"); } finally { setExporting(false); }
  };

  useEffect(() => {
    let stale = false;
    api.get("/insights/last", { params: { period } }).then((r) => { if (!stale) setData(r.data || null); }).catch(() => { if (!stale) setData(null); });
    return () => { stale = true; };
  }, [period]);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.post("/insights", { period });
      setData(res.data);
      toast.success("Analisi generata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella generazione dell'analisi");
    } finally { setLoading(false); }
  };

  const s = data?.stats;
  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto nx-fade-in pb-10">
        <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight mb-1 flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-primary" /> Insights
        </h1>
        <p className="text-sm text-slate-500 mb-6">Analisi AI dei trasporti: volumi, trend, rotte e stato operativo.</p>

        <Card className="border-slate-200 p-4 flex flex-wrap items-center gap-3 mb-6">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="insights-period-select" className="h-9 w-52"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`insights-period-${v}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="insights-generate-button" onClick={generate} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Analisi in corso…" : "Genera analisi"}
          </Button>
          {data && (
            <Button data-testid="insights-export-pdf" variant="outline" onClick={exportPdf} disabled={exporting} className="gap-2">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Esporta PDF
            </Button>
          )}
          {data && (
            <span data-testid="insights-generated-at" className="text-xs text-slate-500 ml-auto">
              Ultima analisi: {formatDateTime(data.generated_at)} · {data.generated_by} · {data.stats.total} trasporti
            </span>
          )}
        </Card>

        {!data && !loading && (
          <Card data-testid="insights-empty" className="border-dashed border-slate-300 p-10 text-center text-sm text-slate-500">
            Nessuna analisi per questo periodo. Premi <strong>Genera analisi</strong> per avviarla.
          </Card>
        )}

        {data && (
          <div className="space-y-6">
            <Card className="border-primary/30 bg-primary/5 p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Commento operativo · {data.period_label}</h2>
              <Narrative text={data.narrative} />
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              <ChartCard title="Volumi nel tempo" icon={CalendarDays} testId="insights-volumes-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={s.volumes}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
                    <Line type="monotone" dataKey="count" name="Trasporti" stroke="#18314F" strokeWidth={2} /></LineChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Giorni di picco" icon={CalendarDays} testId="insights-weekday-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={s.by_weekday}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
                    <Bar dataKey="count" name="Trasporti" fill="#18314F" radius={[4, 4, 0, 0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Orari di picco" icon={Clock} testId="insights-hour-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={s.by_hour}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={2} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} /><Tooltip />
                    <Bar dataKey="count" name="Trasporti" fill="#C6AD76" radius={[4, 4, 0, 0]} /></BarChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Rotte più frequenti" icon={Route} testId="insights-routes"><TopList items={s.routes} testId="insights-routes-list" /></ChartCard>
              <ChartCard title="Tipologie mezzo" icon={Truck} testId="insights-mezzi"><TopList items={s.mezzi} testId="insights-mezzi-list" /></ChartCard>
              <ChartCard title="Mobilizzazione e causali" icon={Truck} testId="insights-mob">
                <div className="grid grid-cols-2 gap-4">
                  <TopList items={s.mobilizzazione} testId="insights-mob-list" /><TopList items={s.causali} testId="insights-causali-list" />
                </div>
              </ChartCard>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
